import { inject, Injectable } from '@angular/core';
import { EnvService } from '@core-mkt/services/env/env.service';
import type { PillContent } from './product-pill-content';
import { PRODUCT_PILL_CONTENT } from './product-pill-content';

/**
 * Shared resolution logic for product pills. The component renders them; card
 * consumers use the service to ask "given these pill values from CMS, what's
 * the active pill content for the current brand?" so the card root can apply
 * its matching border class and positioning context.
 *
 * If multiple pills are visible at once we surface them all in `getVisiblePills`;
 * `getActivePillContent` returns the first one, which is what card-border
 * styling keys off (one card, one border style).
 */
@Injectable({ providedIn: 'root' })
export class ProductPillService {
  private readonly env = inject(EnvService);

  // designMode/brandId are fixed for a given brand build, but `EnvService.get` cloneDeeps the
  // entire env on every access — so read `brandConfig` once here and derive both from it,
  // rather than deep-cloning repeatedly while many cards resolve their pills.
  private readonly brandConfig = this.env.get.brandConfig;
  private readonly designMode = this.brandConfig.designMode ?? 'ace';
  // Normalize brand id casing so the `brandIds` allow-list can compare case-insensitively:
  // brandConfig.id is 'DEC' in the real configs, but some test/env sources use 'dec'.
  private readonly brandId = this.brandConfig.id?.toUpperCase();

  getVisiblePills(pills: string[] | null | undefined): Array<{ key: string; content: PillContent }> {
    const keys = (pills ?? []).filter(
      (value): value is string => typeof value === 'string' && Object.hasOwn(PRODUCT_PILL_CONTENT, value),
    );
    return (
      // De-dupe (preserving order) so a repeated CMS value can't render two identical pills or
      // break the `@for (...; track pill.key)` uniqueness.
      [...new Set(keys)]
        .map((key) => ({ key, content: PRODUCT_PILL_CONTENT[key] }))
        .filter(({ content }) => !content.designMode || content.designMode === this.designMode)
        // Brand allow-list: a pill with `brandIds` renders only on those brands. Guards
        // against a pill leaking onto another brand that shares the same designMode — e.g.
        // 'smart-practice' is DEC-only, but DEC and AARP both run designMode 'default'.
        // Compared upper-case on both sides (brandId is normalized above) to tolerate casing.
        .filter(({ content }) => !content.brandIds || content.brandIds.some((id) => id.toUpperCase() === this.brandId))
    );
  }

  getActivePillContent(pills: string[] | null | undefined): PillContent | null {
    return this.getVisiblePills(pills)[0]?.content ?? null;
  }

  getCardBorderClass(pills: string[] | null | undefined): string {
    return this.getActivePillContent(pills)?.cardBorderClass ?? '';
  }
}
